/* @flow */

import * as React from 'react';
import { compose, withHandlers, withProps } from 'recompose';
import glamorous from 'glamorous';
import Helmet from 'react-helmet';
import t from 'app/utils/translate';
import PaginationBox from 'app/components/commons/PaginationBox';
import withAPICollection from 'app/hoc/withAPICollection';
import { getMyChallenges } from 'app/api/challenges';
import ChallengesList from '../../components/challenges/ChallengesList';
import Loader from '../../components/commons/Loader';
import { POSITION, POINTS, EXPAND, WON } from '../../constants/columnTypes';
import decodeChallenge from '../../normalizers/decodeChallenge';
import AccountHistoryFilterBar from './AccountHistoryFilterBar';

type Props = {
  currentPage: number,
  isLoading: boolean,
  items: Array<*>,
  lastPage?: number,
  onFetch: (?number) => void
};

const Wrapper = glamorous.div(({ theme }) => ({
  marginTop: theme.spacing.tiny,
  [theme.mediaQuery.desktopAndUp]: {
    marginTop: theme.spacing.tiny
  }
}));

class AccountHistory extends React.Component<Props> {
  props: Props;

  state = {
    expandedChallengeId: null
  };

  static defaultProps = {
    lastPage: undefined
  };

  componentDidMount() {
    const { onFetch } = this.props;
    onFetch();
  }

  handleExpand = (id: string) => {
    this.setState(state => ({
      expandedChallengeId: id !== state.expandedChallengeId ? id : undefined
    }));
  };

  render() {
    const { currentPage, isLoading, items, lastPage, onFetch } = this.props;
    const { expandedChallengeId } = this.state;
    const columns = [POSITION, POINTS, WON, EXPAND];
    const emptyMessage = t('The completed challenges that you entered will appear here');
    const emptyTitle = t('No completed challenges');
    const expandType = 'summary';
    const title = t('Completed challenges');
    return (
      <Wrapper>
        <Helmet title={t('History')} />
        <AccountHistoryFilterBar />
        {lastPage > 1 && (
          <PaginationBox currentPage={currentPage} lastPage={lastPage} onChange={onFetch} />
        )}
        <div>
          {isLoading ? (
            <Loader />
          ) : (
            <ChallengesList
              challenges={items}
              columns={columns}
              emptyMessage={emptyMessage}
              emptyTitle={emptyTitle}
              expandedChallengeId={expandedChallengeId}
              expandType={expandType}
              onExpand={this.handleExpand}
              showPlaceholder
              title={title}
            />
          )}
        </div>
        {lastPage > 1 && !isLoading && (
          <PaginationBox currentPage={currentPage} lastPage={lastPage} onChange={onFetch} />
        )}
      </Wrapper>
    );
  }
}

const enhance = compose(
  withAPICollection({
    fetcher: getMyChallenges,
    normalizeData: (data, meta) => data.map(ch => decodeChallenge(ch, meta))
  }),
  withProps(props => ({
    isLoading: props.loading
  })),
  withHandlers({
    onFetch: ({ onFetch }) => page => {
      onFetch({
        embed: ['current_user_score'],
        page,
        status: 'closed',
        sort_by: 'start_time:desc'
      });
    }
  })
);

export default enhance(AccountHistory);
